(function () {
  'use strict';

  /**
   * DK postnummer → landsdel (grove bånd, til sadelprøvning-gruppering).
   * 1000–2999 Hovedstaden, 3000–4999 Sjælland/øerne,
   * 5000–5999 Fyn, 6000–9999 Jylland.
   */
  function landsdelFromPostnummer(raw) {
    const digits = normalizePostnummer(raw);
    if (digits.length !== 4) return '';
    const n = parseInt(digits, 10);
    if (n < 1000 || n > 9999) return '';
    if (n <= 2999) return 'Hovedstaden';
    if (n <= 4999) return 'Sjælland';
    if (n <= 5999) return 'Fyn';
    return 'Jylland';
  }

  function normalizePostnummer(raw) {
    return String(raw || '').replace(/\D/g, '').slice(0, 4);
  }

  function normalizePhone(raw) {
    return String(raw || '').replace(/[^\d+]/g, '');
  }

  /** Danish mobile/landline: 8 digits, optional +45 / 0045 / 45. */
  function isValidDkPhone(raw) {
    const cleaned = normalizePhone(raw);
    if (!cleaned) return false;
    let digits = cleaned;
    if (digits.startsWith('+45')) digits = digits.slice(3);
    else if (digits.startsWith('0045')) digits = digits.slice(4);
    else if (digits.startsWith('45') && digits.length === 10) digits = digits.slice(2);
    digits = digits.replace(/\D/g, '');
    return /^\d{8}$/.test(digits);
  }

  function formatDkPhone(raw) {
    const cleaned = normalizePhone(raw);
    let digits = cleaned;
    if (digits.startsWith('+45')) digits = digits.slice(3);
    else if (digits.startsWith('0045')) digits = digits.slice(4);
    else if (digits.startsWith('45') && digits.length === 10) digits = digits.slice(2);
    digits = digits.replace(/\D/g, '').slice(0, 8);
    return digits;
  }

  function isValidEmail(raw) {
    const email = String(raw || '').trim();
    // Practical RFC-ish check — requires local@domain.tld
    return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/.test(
      email
    );
  }

  function isValidPostnummer(raw) {
    const digits = normalizePostnummer(raw);
    if (!/^\d{4}$/.test(digits)) return false;
    const n = parseInt(digits, 10);
    return n >= 1000 && n <= 9999;
  }

  function trackKlaviyo(publicKey, email, properties) {
    if (!publicKey || !email) return Promise.resolve();

    var phoneE164 =
      properties.telefon && String(properties.telefon).length === 8
        ? '+45' + properties.telefon
        : undefined;

    // Persist on the profile so landsdel-segmenter / overblik virker i Klaviyo
    // (ikke kun på det enkelte event).
    var profileAttributes = {
      email: email,
      first_name: properties.navn || undefined,
      phone_number: phoneE164,
      location: properties.postnummer
        ? { zip: properties.postnummer, country: 'Denmark' }
        : undefined,
      properties: {
        sadelprove_status: 'venter',
        sadelprove_landsdel: properties.landsdel || '',
        sadelprove_postnummer: properties.postnummer || '',
        sadelprove_telefon: properties.telefon || '',
        sadelprove_oensket_model: properties.oensket_model || '',
        sadelprove_source: 'sadelprove-form',
      },
    };

    try {
      window._learnq = window._learnq || [];
      window._learnq.push([
        'identify',
        {
          $email: email,
          email: email,
          $first_name: properties.navn || undefined,
          $phone_number: phoneE164,
          sadelprove_status: 'venter',
          sadelprove_landsdel: properties.landsdel || '',
          sadelprove_postnummer: properties.postnummer || '',
          sadelprove_telefon: properties.telefon || '',
          sadelprove_oensket_model: properties.oensket_model || '',
        },
      ]);
      window._learnq.push(['track', 'Sadelprøvning Signup', properties]);
    } catch (_) {
      /* ignore */
    }

    return fetch(
      `https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(publicKey)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2024-10-15',
        },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: profileAttributes,
                },
              },
              metric: {
                data: {
                  type: 'metric',
                  attributes: { name: 'Sadelprøvning Signup' },
                },
              },
              properties: properties,
            },
          },
        }),
      }
    ).catch(function () {
      /* Klaviyo optional until key is set */
    });
  }

  function payloadFromForm(form) {
    const fd = new FormData(form);
    const postnummer = normalizePostnummer(fd.get('postnummer'));
    const landsdel =
      String(fd.get('landsdel') || '').trim() || landsdelFromPostnummer(postnummer);
    return {
      navn: String(fd.get('navn') || '').trim(),
      email: String(fd.get('email') || '').trim().toLowerCase(),
      telefon: formatDkPhone(fd.get('telefon')),
      postnummer: postnummer,
      landsdel: landsdel,
      oensket_model: String(fd.get('oensket_model') || '').trim(),
      besked: String(fd.get('besked') || '').trim(),
      website: String(fd.get('website') || '').trim(),
    };
  }

  var FIELD_MESSAGES = {
    navn: 'Skriv dit navn.',
    email: 'Skriv en gyldig e-mail (fx navn@mail.dk).',
    telefon: 'Skriv et gyldigt dansk telefonnummer (8 cifre).',
    postnummer: 'Skriv et gyldigt 4-cifret postnummer.',
  };

  function fieldError(name, data) {
    if (name === 'navn') {
      if (!data.navn || data.navn.length < 2) return FIELD_MESSAGES.navn;
      return '';
    }
    if (name === 'email') {
      if (!isValidEmail(data.email)) return FIELD_MESSAGES.email;
      return '';
    }
    if (name === 'telefon') {
      if (!isValidDkPhone(data.telefon)) return FIELD_MESSAGES.telefon;
      return '';
    }
    if (name === 'postnummer') {
      if (!isValidPostnummer(data.postnummer)) return FIELD_MESSAGES.postnummer;
      if (!landsdelFromPostnummer(data.postnummer)) {
        return 'Postnummeret ser ikke ud til at være gyldigt.';
      }
      return '';
    }
    return '';
  }

  function validateAll(data) {
    if (data.website) return { ok: false, message: 'Spam fanget.', field: null };
    var order = ['navn', 'email', 'telefon', 'postnummer'];
    for (var i = 0; i < order.length; i++) {
      var err = fieldError(order[i], data);
      if (err) return { ok: false, message: err, field: order[i] };
    }
    return { ok: true, message: '', field: null };
  }

  async function submitContact(form, data) {
    const body = new FormData();
    body.append('form_type', 'contact');
    body.append('utf8', '✓');
    body.append('contact[email]', data.email);
    body.append('contact[name]', data.navn);
    body.append('contact[phone]', data.telefon);
    body.append(
      'contact[body]',
      [
        'Sadelprøvning-tilmelding',
        'Navn: ' + data.navn,
        'E-mail: ' + data.email,
        'Telefon: ' + data.telefon,
        'Postnummer: ' + data.postnummer,
        'Landsdel: ' + data.landsdel,
        'Ønsket model: ' + (data.oensket_model || '—'),
        'Besked: ' + (data.besked || '—'),
        'Tag: sadelprove',
      ].join('\n')
    );

    await fetch(form.getAttribute('action') || '/contact', {
      method: 'POST',
      body: body,
      headers: { Accept: 'application/json' },
    });
    return true;
  }

  async function submitWebhook(url, data) {
    if (!url) return null;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        source: 'barefoot-sadelprove',
        tag: 'sadelprove',
        ...data,
      }),
    });
    if (!res.ok) throw new Error('Webhook ' + res.status);
    return res.json().catch(function () {
      return { ok: true };
    });
  }

  function showSuccessState(root, formPanel, successEl, depositEl) {
    root.classList.add('is-complete');
    if (formPanel) {
      formPanel.classList.add('is-collapsing');
      window.setTimeout(function () {
        formPanel.classList.add('is-collapsed');
        formPanel.setAttribute('hidden', '');
      }, 480);
    }
    if (successEl) {
      successEl.hidden = false;
      // Force reflow so transition runs
      void successEl.offsetWidth;
      successEl.classList.add('is-visible');
    }
    if (depositEl) depositEl.hidden = true;
  }

  function init(root) {
    const form =
      root.querySelector('#sadelprove-contact-form') ||
      root.querySelector('form.sadelprove__form');
    if (!form) return;

    const formPanel = root.querySelector('[data-sadelprove-form-panel]');
    const postInput = form.querySelector('[name="postnummer"]');
    const phoneInput = form.querySelector('[name="telefon"]');
    const emailInput = form.querySelector('[name="email"]');
    const nameInput = form.querySelector('[name="navn"]');
    const landsdelInput = form.querySelector('[name="landsdel"]');
    const landsdelOut = root.querySelector('[data-sadelprove-landsdel]');
    const statusEl = root.querySelector('[data-sadelprove-status]');
    const successEl = root.querySelector('[data-sadelprove-success]');
    const depositEl = root.querySelector('[data-sadelprove-deposit]');
    const submitBtn =
      root.querySelector('[data-sadelprove-submit]') ||
      form.querySelector('button[type="submit"], button[type="button"]');

    function fieldWrap(input) {
      return input ? input.closest('.sadelprove__field') : null;
    }

    function setFieldState(input, error) {
      const wrap = fieldWrap(input);
      if (!input || !wrap) return;
      const msg = wrap.querySelector('[data-sadelprove-field-error]');
      if (error) {
        input.setAttribute('aria-invalid', 'true');
        wrap.classList.add('sadelprove__field--invalid');
        if (msg) {
          msg.hidden = false;
          msg.textContent = error;
        }
      } else {
        input.removeAttribute('aria-invalid');
        wrap.classList.remove('sadelprove__field--invalid');
        if (msg) {
          msg.hidden = true;
          msg.textContent = '';
        }
      }
    }

    function syncLandsdel() {
      if (postInput) {
        const digits = normalizePostnummer(postInput.value);
        if (postInput.value !== digits) postInput.value = digits;
      }
      const ld = landsdelFromPostnummer(postInput && postInput.value);
      if (landsdelInput) landsdelInput.value = ld;
      if (landsdelOut) {
        landsdelOut.textContent = ld
          ? 'Landsdel: ' + ld
          : 'Landsdel beregnes ud fra postnummer';
      }
    }

    function validateField(input) {
      if (!input) return true;
      const data = payloadFromForm(form);
      if (input.name === 'postnummer') data.postnummer = normalizePostnummer(input.value);
      if (input.name === 'telefon') data.telefon = formatDkPhone(input.value);
      if (input.name === 'email') data.email = String(input.value || '').trim().toLowerCase();
      if (input.name === 'navn') data.navn = String(input.value || '').trim();

      const err = fieldError(input.name, data);
      setFieldState(input, err);
      return !err;
    }

    if (postInput) {
      postInput.addEventListener('input', function () {
        syncLandsdel();
        if (postInput.hasAttribute('aria-invalid')) validateField(postInput);
      });
      postInput.addEventListener('blur', function () {
        validateField(postInput);
      });
      syncLandsdel();
    }

    if (phoneInput) {
      phoneInput.addEventListener('input', function () {
        var v = phoneInput.value.replace(/[^\d+\s()-]/g, '');
        if (phoneInput.value !== v) phoneInput.value = v;
        if (phoneInput.hasAttribute('aria-invalid')) validateField(phoneInput);
      });
      phoneInput.addEventListener('blur', function () {
        phoneInput.value = formatDkPhone(phoneInput.value);
        validateField(phoneInput);
      });
    }

    if (emailInput) {
      emailInput.addEventListener('blur', function () {
        emailInput.value = String(emailInput.value || '').trim().toLowerCase();
        validateField(emailInput);
      });
      emailInput.addEventListener('input', function () {
        if (emailInput.hasAttribute('aria-invalid')) validateField(emailInput);
      });
    }

    if (nameInput) {
      nameInput.addEventListener('blur', function () {
        validateField(nameInput);
      });
      nameInput.addEventListener('input', function () {
        if (nameInput.hasAttribute('aria-invalid')) validateField(nameInput);
      });
    }

    // Hard-block native Shopify contact navigation
    form.setAttribute('novalidate', 'novalidate');
    form.addEventListener(
      'submit',
      function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        handleSubmit();
        return false;
      },
      true
    );

    async function handleSubmit() {
      syncLandsdel();
      if (phoneInput) phoneInput.value = formatDkPhone(phoneInput.value);

      const data = payloadFromForm(form);
      const result = validateAll(data);

      setFieldState(nameInput, fieldError('navn', data));
      setFieldState(emailInput, fieldError('email', data));
      setFieldState(phoneInput, fieldError('telefon', data));
      setFieldState(postInput, fieldError('postnummer', data));

      if (statusEl) {
        statusEl.hidden = result.ok;
        statusEl.textContent = result.ok ? '' : result.message;
        statusEl.classList.toggle('sadelprove__status--error', !result.ok);
      }
      if (!result.ok) {
        var focusEl =
          (result.field && form.querySelector('[name="' + result.field + '"]')) ||
          form.querySelector('[aria-invalid="true"]');
        if (focusEl) focusEl.focus();
        return;
      }

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-busy', 'true');
      }

      try {
        await submitContact(form, data);
        await submitWebhook(root.dataset.submitUrl || '', data);
        await trackKlaviyo(root.dataset.klaviyoPublicKey || '', data.email, {
          navn: data.navn,
          postnummer: data.postnummer,
          landsdel: data.landsdel,
          telefon: data.telefon,
          oensket_model: data.oensket_model,
          besked: data.besked,
        });

        if (statusEl) statusEl.hidden = true;
        showSuccessState(root, formPanel, successEl, depositEl);
      } catch (ex) {
        if (statusEl) {
          statusEl.hidden = false;
          statusEl.textContent =
            'Noget gik galt — prøv igen, eller skriv til os på e-mail.';
          statusEl.classList.add('sadelprove__status--error');
        }
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.removeAttribute('aria-busy');
        }
      }
    }

    if (submitBtn) {
      submitBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit();
      });
    }
  }

  document.querySelectorAll('[data-sadelprove]').forEach(init);
})();
